import * as THREE from 'three';
import { edgeLines, markOverlay, setEdgeStyle } from './edges';
import type { EdgeLines } from './edges';
import { edgeColor, modelColor } from './palette';

const SIDE_MARGIN = 1.25;
const THICKNESS_RATIO = 0.025;
const TOP_GAP_RATIO = 0.001;
const GHOST_OPACITY = 0.3;
const RIM_OPACITY = 0.42;
// Behind the part's faces and the contact shadow, so the part's base and its
// shadow win where they meet the plate's top.
const PLATFORM_DEPTH_OFFSET = 4;

export interface FloorRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface PlatformStyle {
  faces: boolean;
  rim: boolean;
  ghosted: boolean;
}

/** The plate's side: the part's footprint with a margin, rounded up to a step of 5 at its scale. */
export function platformSide(footprint: number): number {
  const side = Math.max(footprint, 1) * SIDE_MARGIN;
  const step = 5 * 10 ** (Math.floor(Math.log10(side)) - 1);
  return Math.ceil(side / step - 1e-9) * step;
}

function rimSegments(): THREE.BufferGeometry {
  return new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1));
}

/** The machine bed the part stands on; its top face is the floor. */
export class BuildPlatform {
  readonly object = new THREE.Group();
  private readonly plate: THREE.Mesh<THREE.BoxGeometry, THREE.MeshStandardMaterial>;
  private readonly rim: EdgeLines;
  private rect: FloorRect | null = null;
  private shown = true;
  private style: PlatformStyle = { faces: true, rim: true, ghosted: false };

  constructor(isDark: boolean) {
    this.plate = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshStandardMaterial({
        color: modelColor(isDark),
        roughness: 0.6,
        metalness: 0.05,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: PLATFORM_DEPTH_OFFSET,
        polygonOffsetUnits: PLATFORM_DEPTH_OFFSET,
      }),
    );
    this.rim = edgeLines(rimSegments(), 1);
    setEdgeStyle(this.rim, edgeColor(isDark), RIM_OPACITY);
    this.object.add(markOverlay(this.plate), markOverlay(this.rim));
    this.object.visible = false;
  }

  /** Where the plate covers the floor, while it is drawn opaque; the grid leaves it bare. */
  get cover(): FloorRect | null {
    return this.object.visible && this.style.faces && !this.style.ghosted ? this.rect : null;
  }

  place(box: THREE.Box3 | null): void {
    if (!box || box.isEmpty()) {
      this.rect = null;
      this.refresh();
      return;
    }
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const side = platformSide(Math.max(size.x, size.y));
    const thickness = side * THICKNESS_RATIO;
    const top = Math.min(0, box.min.z) - side * TOP_GAP_RATIO;
    this.object.position.set(center.x, center.y, top - thickness / 2);
    this.object.scale.set(side, side, thickness);
    this.rect = {
      minX: center.x - side / 2,
      minY: center.y - side / 2,
      maxX: center.x + side / 2,
      maxY: center.y + side / 2,
    };
    this.refresh();
  }

  show(shown: boolean): void {
    this.shown = shown;
    this.refresh();
  }

  setStyle(style: PlatformStyle): void {
    this.style = style;
    const material = this.plate.material;
    material.transparent = style.ghosted;
    material.opacity = style.ghosted ? GHOST_OPACITY : 1;
    material.depthWrite = !style.ghosted;
    material.needsUpdate = true;
    this.refresh();
  }

  setClip(plane: THREE.Plane | null): void {
    const planes = plane ? [plane] : null;
    this.plate.material.clippingPlanes = planes;
    this.rim.material.clippingPlanes = planes;
  }

  dispose(): void {
    this.object.removeFromParent();
    this.plate.geometry.dispose();
    this.plate.material.dispose();
    this.rim.geometry.dispose();
    this.rim.material.dispose();
  }

  private refresh(): void {
    this.object.visible = this.shown && this.rect !== null;
    this.plate.visible = this.style.faces;
    this.rim.visible = this.style.rim;
  }
}
